/**
 * Pure business-logic utilities for shift validation.
 * These functions contain no React dependencies and can be tested via Jest directly.
 *
 * Mirrors the rules documented in FUNCTIONAL_DOCUMENTATION.md §7.2 Schedule Business Rules.
 */

import { IShiftPayload } from '../interfaces/ShiftsInterface';

export interface TimeRange {
  startAt: string;
  endAt: string;
}

export interface ShiftLengthSettings {
  defaultShiftLengthHours?: number;
  minShiftLength?: number;
  maxShiftLength?: number;
}

export const DEFAULT_MIN_SHIFT_LENGTH_HOURS = 2;
export const DEFAULT_MAX_SHIFT_LENGTH_HOURS = 16;

/**
 * Returns the configured default shift duration in hours, floored at the minimum shift length.
 */
export function getDefaultShiftDurationHours(settings?: ShiftLengthSettings): number {
  const minHours = settings?.minShiftLength || DEFAULT_MIN_SHIFT_LENGTH_HOURS;
  const defaultHours = settings?.defaultShiftLengthHours ?? 8;
  return Math.max(defaultHours, minHours);
}

/**
 * Returns a user-facing error when the shift duration is outside the configured min/max.
 * Invalid time ranges (end before start) are left to other validators.
 */
export function getShiftLengthValidationError(
  startAt: string,
  endAt: string,
  settings?: ShiftLengthSettings,
): string | null {
  if (!isShiftTimeValid(startAt, endAt)) {
    return null;
  }

  const minHours = settings?.minShiftLength || DEFAULT_MIN_SHIFT_LENGTH_HOURS;
  const maxHours = settings?.maxShiftLength || DEFAULT_MAX_SHIFT_LENGTH_HOURS;
  const durationHours = getShiftDurationHours(startAt, endAt);

  if (durationHours < minHours) {
    return `Shift length must be at least ${minHours} hours`;
  }
  if (durationHours > maxHours) {
    return `Shift length cannot exceed ${maxHours} hours`;
  }

  return null;
}

/**
 * Ensures a proposed shift end time meets the minimum shift length.
 * Selections shorter than the minimum use the configured default shift length instead.
 */
export function resolveShiftEndTime(
  start: Date | string,
  end: Date | string,
  settings?: ShiftLengthSettings,
): Date {
  const startIso = typeof start === 'string' ? start : start.toISOString();
  const endIso = typeof end === 'string' ? end : end.toISOString();
  const minHours = settings?.minShiftLength || DEFAULT_MIN_SHIFT_LENGTH_HOURS;
  const durationHours = getShiftDurationHours(startIso, endIso);

  if (durationHours >= minHours) {
    return typeof end === 'string' ? new Date(end) : new Date(end.getTime());
  }

  const resolvedEnd = new Date(typeof start === 'string' ? start : start.getTime());
  resolvedEnd.setTime(
    resolvedEnd.getTime() + getDefaultShiftDurationHours(settings) * 60 * 60 * 1000,
  );
  return resolvedEnd;
}

// ─── Time validity ─────────────────────────────────────────────────────────────

/**
 * A shift is valid when endAt is strictly after startAt.
 */
export function isShiftTimeValid(startAt: string, endAt: string): boolean {
  const start = new Date(startAt).getTime();
  const end = new Date(endAt).getTime();
  return !isNaN(start) && !isNaN(end) && end > start;
}

/**
 * Returns the duration in decimal hours.
 */
export function getShiftDurationHours(startAt: string, endAt: string): number {
  const start = new Date(startAt).getTime();
  const end = new Date(endAt).getTime();
  return (end - start) / (1000 * 60 * 60);
}

// ─── Overlap detection ────────────────────────────────────────────────────────

/**
 * Returns true when two time ranges overlap (open-interval comparison).
 *   aStart < bEnd  AND  bStart < aEnd
 */
export function doRangesOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  const aS = new Date(aStart).getTime();
  const aE = new Date(aEnd).getTime();
  const bS = new Date(bStart).getTime();
  const bE = new Date(bEnd).getTime();
  return aS < bE && bS < aE;
}

/**
 * Returns true when the proposed shift overlaps with any existing shift.
 */
export function hasShiftOverlap(
  proposed: TimeRange,
  existingShifts: TimeRange[],
): boolean {
  return existingShifts.some((s) =>
    doRangesOverlap(proposed.startAt, proposed.endAt, s.startAt, s.endAt),
  );
}

// ─── Conflict checks ──────────────────────────────────────────────────────────

/**
 * Hard block: returns true if the shift falls within an approved time-off window.
 */
export function conflictsWithTimeOff(
  shiftStart: string,
  shiftEnd: string,
  timeOffPeriods: TimeRange[],
): boolean {
  return timeOffPeriods.some((t) =>
    doRangesOverlap(shiftStart, shiftEnd, t.startAt, t.endAt),
  );
}

/**
 * Soft warning: returns true if the shift falls during a declared unavailability.
 */
export function conflictsWithUnavailability(
  shiftStart: string,
  shiftEnd: string,
  unavailabilities: TimeRange[],
): boolean {
  return unavailabilities.some((u) =>
    doRangesOverlap(shiftStart, shiftEnd, u.startAt, u.endAt),
  );
}

// ─── Labor cost ───────────────────────────────────────────────────────────────

/**
 * Calculates the base labor cost for a shift.
 * @param hourlyRate  - dollars per hour
 * Returns cost in dollars, rounded to 2 decimal places.
 */
export function calculateLaborCost(
  hourlyRate: number,
  startAt: string,
  endAt: string,
): number {
  const hours = getShiftDurationHours(startAt, endAt);
  return Math.round(hourlyRate * hours * 100) / 100;
}

/**
 * Calculates overtime hours (hours beyond weeklyThreshold within the week totals).
 */
export function calculateOvertimeHours(
  totalWeeklyHours: number,
  weeklyThreshold = 40,
): number {
  return Math.max(0, totalWeeklyHours - weeklyThreshold);
}

// ─── Full payload validation ───────────────────────────────────────────────────

/**
 * Validates an IShiftPayload and returns an array of error messages.
 * Empty array means the payload is valid.
 */
export function validateShiftPayload(payload: IShiftPayload): string[] {
  const errors: string[] = [];

  if (!payload.workplace?.trim()) {
    errors.push('Workplace is required.');
  }
  if (!payload.startAt || !payload.endAt) {
    errors.push('Start time and end time are required.');
    return errors; // cannot proceed without both times
  }
  if (!isShiftTimeValid(payload.startAt, payload.endAt)) {
    errors.push('Shift end time must be after start time.');
  }

  return errors;
}
